import type { ModuleManifestInput } from '@portal/kernel';
import type { ComponentType } from 'react';
import type { SlotLoader, SlotName } from './slots.js';

/** Props the dashboard passes to a widget: its title from the manifest, in the UI language. */
export interface WidgetProps {
  title: string;
}
export type WidgetLoader = () => Promise<{ default: ComponentType<WidgetProps> }>;

export interface WebRoute {
  /** Absolute path, must start with the module's own prefix, e.g. `/youtube/channels`. Supports `$param` segments. */
  path: string;
  /** Lazily loaded page, so each module ships in its own chunk. */
  component: () => Promise<{ default: ComponentType }>;
  /** `app` (default): inside the shell with sidebar. `blank`: full-screen page (login, waiting room). */
  layout?: 'app' | 'blank';
  /** Reachable without signing in (login page). */
  public?: boolean;
  /** Reachable by signed-in users whose account is restricted (pending approval, 2FA not enrolled). */
  allowRestricted?: boolean;
}

/** Front-end half of a module: its manifest plus the pages it contributes to the shell. */
export interface WebModuleDefinition {
  manifest: ModuleManifestInput;
  routes: WebRoute[];
  /**
   * UI strings of the module, registered as the i18next namespace `<module key>`.
   * Use them with `useTranslation('<module key>')`. Vietnamese is required, English falls back to it.
   */
  translations?: { vi: Record<string, unknown>; en?: Record<string, unknown> };
  /**
   * Dashboard widgets declared in the manifest (`widgets`), by key. Each loads lazily and fetches its
   * own data through the module's API; the dashboard decides which ones a user sees.
   */
  widgets?: Record<string, WidgetLoader>;
  /**
   * Counts shown next to menu entries (manifest `navigation` ids), e.g. unread chat messages. Each is a
   * React hook returning the count (0 hides it); it runs only while the entry is shown.
   */
  navBadges?: Record<string, () => number>;
  /** Controls the module puts into other modules' pages (see `SlotProps`), loaded lazily. */
  slots?: { [N in SlotName]?: SlotLoader<N> };
}

export function defineWebModule(definition: WebModuleDefinition): WebModuleDefinition {
  for (const route of definition.routes) {
    if (!route.path.startsWith('/')) {
      throw new Error(
        `Module "${definition.manifest.key}": route "${route.path}" must start with "/"`,
      );
    }
  }
  const declared = new Set((definition.manifest.widgets ?? []).map((w) => w.key));
  const provided = Object.keys(definition.widgets ?? {});
  for (const key of provided) {
    if (!declared.has(key)) {
      throw new Error(
        `Module "${definition.manifest.key}": widget "${key}" is not in the manifest`,
      );
    }
  }
  for (const key of declared) {
    if (!provided.includes(key)) {
      throw new Error(`Module "${definition.manifest.key}": widget "${key}" has no component`);
    }
  }
  return definition;
}
